'use client';

import { useEffect, useRef, useState } from 'react';
import type { Palette } from './types';

//gg

type CommutesSectionProps = {
  palette: Palette;
  id?: string;
};

type AddressSuggestion = {
  placeId: string;
  text: string;
  secondaryText?: string;
};

const ADDRESS_KEY = 'homepros_quote_address';
const SCRIPT_ID = 'homepros-google-maps-js';
const DEFAULT_CENTER = { lat: 29.4241, lng: -98.4936 }; // San Antonio
const DEFAULT_DESTINATION_ADDRESS = '318 W Nakoma Dr, San Antonio, TX 78216';
const SAN_ANTONIO_CENTER = { lat: 29.4241, lng: -98.4936 };
const sanitizeEnv = (value: string | undefined) => (value || '').trim().replace(/^['"]+|['"]+$/g, '');
const MAPS_API_KEY = sanitizeEnv(
  process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || process.env.NEXT_PUBLIC_GOOGLE_API_KEY
);
const DESTINATION_ADDRESS = sanitizeEnv(process.env.NEXT_PUBLIC_HOME_PROS_DESTINATION_ADDRESS) || DEFAULT_DESTINATION_ADDRESS;

declare global {
  interface Window {
    google?: any;
  }
}

function loadMapsScript(apiKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('no-window'));
    if (window.google?.maps) return resolve();

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('maps-load-error')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('maps-load-error'));
    document.head.appendChild(script);
  });
}

export function CommutesSection({ palette, id = 'commutes' }: CommutesSectionProps) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const mapInstRef = useRef<any>(null);
  const geocoderRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const routeLineRef = useRef<any>(null);
  const destinationLatLngRef = useRef<{ lat: number; lng: number } | null>(null);
  const lastLoggedAddressRef = useRef('');
  const searchTimerRef = useRef<number | null>(null);

  const [mapsReady, setMapsReady] = useState(false);
  const [mapsError, setMapsError] = useState('');
  const [routeError, setRouteError] = useState('');
  const [etaText, setEtaText] = useState('');
  const [distanceText, setDistanceText] = useState('');
  const [address, setAddress] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [suppressSuggestions, setSuppressSuggestions] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = window.localStorage.getItem(ADDRESS_KEY);
    setAddress(saved || '');
  }, []);

  useEffect(() => {
    if (!MAPS_API_KEY) {
      setMapsError('Map is not available yet. Add NEXT_PUBLIC_GOOGLE_MAPS_API_KEY to enable it.');
      return;
    }

    let mounted = true;
    loadMapsScript(MAPS_API_KEY)
      .then(() => {
        if (!mounted) return;
        if (!window.google?.maps || !mapRef.current) {
          setMapsError('Google Maps failed to initialize.');
          return;
        }

        const map = new window.google.maps.Map(mapRef.current, {
          center: DEFAULT_CENTER,
          zoom: 11,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });

        mapInstRef.current = map;
        geocoderRef.current = new window.google.maps.Geocoder();
        routeLineRef.current = new window.google.maps.Polyline({
          map,
          path: [],
          strokeColor: palette.forest,
          strokeOpacity: 0.9,
          strokeWeight: 5,
        });
        markerRef.current = new window.google.maps.Marker({
          map,
          position: DEFAULT_CENTER,
          title: 'Your address',
        });

        geocoderRef.current.geocode(
          {
            address: DESTINATION_ADDRESS,
            region: 'us',
            componentRestrictions: { country: 'US' },
          },
          (results: any[], status: string) => {
            if (status !== 'OK' || !results?.length) return;
            const loc = results[0]?.geometry?.location;
            if (!loc) return;
            destinationLatLngRef.current = { lat: loc.lat(), lng: loc.lng() };
          }
        );

        setMapsReady(true);
      })
      .catch(() => {
        if (mounted) setMapsError('Google Maps script failed to load.');
      });

    return () => {
      mounted = false;
    };
  }, [palette.forest]);

  useEffect(() => {
    const query = address.trim();
    if (!mapsReady || query.length < 3 || suppressSuggestions) {
      setSuggestions([]);
      setSearchBusy(false);
      return;
    }

    if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    searchTimerRef.current = window.setTimeout(() => {
      void fetchSuggestions(query);
    }, 250);

    return () => {
      if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    };
  }, [address, mapsReady, suppressSuggestions]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!address.trim()) {
      window.localStorage.removeItem(ADDRESS_KEY);
      return;
    }
    window.localStorage.setItem(ADDRESS_KEY, address);
  }, [address]);

  const decodePolyline = (encoded: string): { lat: number; lng: number }[] => {
    const points: { lat: number; lng: number }[] = [];
    let index = 0;
    let lat = 0;
    let lng = 0;

    while (index < encoded.length) {
      let result = 0;
      let shift = 0;
      let b: number;
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      const deltaLat = (result & 1) ? ~(result >> 1) : (result >> 1);
      lat += deltaLat;

      result = 0;
      shift = 0;
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      const deltaLng = (result & 1) ? ~(result >> 1) : (result >> 1);
      lng += deltaLng;

      points.push({ lat: lat / 1e5, lng: lng / 1e5 });
    }

    return points;
  };

  const parseDurationSeconds = (value?: string) => {
    if (!value) return null;
    const match = value.match(/^(\d+)(?:\.\d+)?s$/);
    if (!match) return null;
    return Number(match[1]);
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return '< 1 min';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    if (hours <= 0) return `${minutes} min`;
    if (minutes <= 0) return `${hours} hr`;
    return `${hours} hr ${minutes} min`;
  };

  const formatDistanceMiles = (meters?: number) => {
    if (typeof meters !== 'number' || Number.isNaN(meters)) return '';
    const miles = meters / 1609.344;
    return `${miles.toFixed(miles >= 10 ? 0 : 1)} mi`;
  };

  const fetchSuggestions = async (query: string) => {
    if (!MAPS_API_KEY) return;
    setSearchBusy(true);
    try {
      const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': MAPS_API_KEY,
          'X-Goog-FieldMask':
            'suggestions.placePrediction.placeId,suggestions.placePrediction.text.text,suggestions.placePrediction.structuredFormat.secondaryText.text',
        },
        body: JSON.stringify({
          input: query,
          includedRegionCodes: ['us'],
          locationBias: {
            circle: {
              center: {
                latitude: SAN_ANTONIO_CENTER.lat,
                longitude: SAN_ANTONIO_CENTER.lng,
              },
              radius: 50000,
            },
          },
        }),
      });

      if (!res.ok) {
        setSuggestions([]);
        return;
      }

      const data = await res.json();
      const raw: AddressSuggestion[] = (data?.suggestions || [])
        .map((s: any) => {
          const p = s?.placePrediction;
          if (!p?.placeId || !p?.text?.text) return null;
          return {
            placeId: p.placeId as string,
            text: p.text.text as string,
            secondaryText: p?.structuredFormat?.secondaryText?.text || '',
          };
        })
        .filter(Boolean);

      const texasOnly = raw.filter((s: any) => {
        const t = `${s.text} ${s.secondaryText}`.toLowerCase();
        return t.includes(' tx') || t.includes('texas');
      });

      setSuggestions((texasOnly.length ? texasOnly : raw).slice(0, 6));
    } catch {
      setSuggestions([]);
    } finally {
      setSearchBusy(false);
    }
  };

  const geocodeAddress = (value: string) => {
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode(
      {
        address: value,
        region: 'us',
        componentRestrictions: { country: 'US' },
      },
      (results: any[], status: string) => {
      if (status !== 'OK' || !results?.length) return;
      const location = results[0].geometry?.location;
      if (!location) return;
      applyResolvedAddress(results[0].formatted_address || value);
      setRouteError('');
      updateOrigin(location);
    });
  };

  const selectSuggestion = async (suggestion: AddressSuggestion) => {
    setAddress(suggestion.text);
    setSuppressSuggestions(true);
    setSuggestions([]);
    try {
      const res = await fetch(`https://places.googleapis.com/v1/places/${encodeURIComponent(suggestion.placeId)}`, {
        headers: {
          'X-Goog-Api-Key': MAPS_API_KEY,
          'X-Goog-FieldMask': 'id,formattedAddress,location',
        },
      });
      if (!res.ok) {
        geocodeAddress(suggestion.text);
        return;
      }
      const data = await res.json();
      const lat = data?.location?.latitude;
      const lng = data?.location?.longitude;
      if (typeof lat !== 'number' || typeof lng !== 'number' || !window.google?.maps) {
        geocodeAddress(suggestion.text);
        return;
      }
      applyResolvedAddress(data?.formattedAddress || suggestion.text);
      setRouteError('');
      updateOrigin(new window.google.maps.LatLng(lat, lng));
    } catch {
      geocodeAddress(suggestion.text);
    }
  };

  const handleAddressSubmit = () => {
    const trimmed = address.trim();
    if (!trimmed) return;
    setSuggestions([]);
    geocodeAddress(trimmed);
  };

  const ensureDestinationLatLng = (callback: () => void) => {
    if (destinationLatLngRef.current) {
      callback();
      return;
    }
    if (!geocoderRef.current) {
      setRouteError('Destination is not ready yet. Please try again.');
      return;
    }
    geocoderRef.current.geocode(
      {
        address: DESTINATION_ADDRESS,
        region: 'us',
        componentRestrictions: { country: 'US' },
      },
      (results: any[], status: string) => {
        if (status !== 'OK' || !results?.length) {
          setRouteError('Unable to locate destination right now.');
          return;
        }
        const loc = results[0]?.geometry?.location;
        if (!loc) {
          setRouteError('Unable to locate destination right now.');
          return;
        }
        destinationLatLngRef.current = { lat: loc.lat(), lng: loc.lng() };
        callback();
      }
    );
  };

  const updateOrigin = (location: any) => {
    if (!mapInstRef.current || !markerRef.current) return;
    markerRef.current.setPosition(location);
    mapInstRef.current.panTo(location);
    ensureDestinationLatLng(() => {
      void routeToDestination(location);
    });
  };

  const routeToDestination = async (originOverride?: any) => {
    if (!routeLineRef.current) return;
    const markerPosition = originOverride || markerRef.current?.getPosition?.();
    if (!markerPosition) return;
    if (!destinationLatLngRef.current) return;

    const origin = { lat: markerPosition.lat(), lng: markerPosition.lng() };
    const destination = destinationLatLngRef.current;

    try {
      const res = await fetch('https://routes.googleapis.com/directions/v2:computeRoutes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': MAPS_API_KEY,
          'X-Goog-FieldMask':
            'routes.polyline.encodedPolyline,routes.duration,routes.staticDuration,routes.distanceMeters',
        },
        body: JSON.stringify({
          origin: {
            location: {
              latLng: {
                latitude: origin.lat,
                longitude: origin.lng,
              },
            },
          },
          destination: {
            location: {
              latLng: {
                latitude: destination.lat,
                longitude: destination.lng,
              },
            },
          },
          travelMode: 'DRIVE',
          routingPreference: 'TRAFFIC_UNAWARE',
          units: 'IMPERIAL',
        }),
      });

      if (!res.ok) {
        setRouteError('Unable to compute route right now. Please confirm your full Texas address.');
        setEtaText('');
        setDistanceText('');
        routeLineRef.current.setPath([]);
        return;
      }

      const data = await res.json();
      const route = data?.routes?.[0];
      const encoded = route?.polyline?.encodedPolyline;
      if (!encoded) {
        setRouteError('No route found for that address.');
        setEtaText('');
        setDistanceText('');
        routeLineRef.current.setPath([]);
        return;
      }

      const durationSeconds =
        parseDurationSeconds(route?.duration) ?? parseDurationSeconds(route?.staticDuration);
      setEtaText(durationSeconds ? formatDuration(durationSeconds) : '');
      setDistanceText(formatDistanceMiles(route?.distanceMeters));

      const path = decodePolyline(encoded);
      routeLineRef.current.setPath(path);
      setRouteError('');
    } catch {
      setRouteError('Unable to compute route right now. Please try again.');
      setEtaText('');
      setDistanceText('');
      routeLineRef.current.setPath([]);
    }
  };

  const normalizeAddress = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();

  const logAddress = async (rawAddress: string) => {
    const trimmed = rawAddress.trim();
    if (trimmed.length < 5) return;
    const normalized = normalizeAddress(trimmed);
    if (lastLoggedAddressRef.current === normalized) return;
    lastLoggedAddressRef.current = normalized;

    try {
      await fetch('/api/home-pros/address-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: trimmed }),
      });
    } catch {
      // Do not block UX if logging fails.
    }
  };

  const applyResolvedAddress = (resolvedAddress: string) => {
    if (!resolvedAddress) return;
    setAddress(resolvedAddress);
    window.localStorage.setItem(ADDRESS_KEY, resolvedAddress);
    void logAddress(resolvedAddress);
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation || !window.google?.maps || !geocoderRef.current) {
      setRouteError('Location services are unavailable in this browser.');
      return;
    }
    setSuppressSuggestions(true);
    setSuggestions([]);
    setGeoBusy(true);
    setRouteError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const location = new window.google.maps.LatLng(pos.coords.latitude, pos.coords.longitude);
        updateOrigin(location);
        geocoderRef.current.geocode({ location }, (results: any[], status: string) => {
          setGeoBusy(false);
          if (status !== 'OK' || !results?.length) return;
          const formatted = results[0]?.formatted_address || '';
          if (!formatted) return;
          setSuppressSuggestions(true);
          setSuggestions([]);
          applyResolvedAddress(formatted);
        });
      },
      () => {
        setGeoBusy(false);
        setRouteError('Unable to access your location. Please allow location permission.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <section id={id} className="px-6 py-12 lg:px-12">
      <div className="mx-auto max-w-7xl rounded-[32px] border border-white/60 bg-white/70 p-6 shadow-xl backdrop-blur lg:p-8">
        <div className="mb-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Commute Planner</p>
          <h3 className="mt-2 text-2xl font-bold text-slate-900">See estimated travel time from your address</h3>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr,2fr]">
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">
                Your Address (Texas, near San Antonio)
              </label>
              <input
                ref={inputRef}
                type="text"
                value={address}
                onChange={(e) => {
                  setSuppressSuggestions(false);
                  setAddress(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddressSubmit();
                  }
                }}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                placeholder="Start typing your address in Texas..."
                autoComplete="street-address"
              />
              {suggestions.length > 0 ? (
                <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow">
                  {suggestions.map((s) => (
                    <button
                      key={s.placeId}
                      type="button"
                      className="block w-full border-b border-slate-100 px-3 py-2 text-left text-sm text-slate-800 last:border-b-0 hover:bg-slate-50"
                      onClick={() => {
                        void selectSuggestion(s);
                      }}
                    >
                      {s.text}
                    </button>
                  ))}
                </div>
              ) : null}
              {searchBusy ? <p className="mt-2 text-xs text-slate-500">Searching places...</p> : null}
              {etaText ? (
                <p className="mt-2 text-sm font-semibold text-slate-800">
                  Estimated drive time: {etaText}
                  {distanceText ? ` (${distanceText})` : ''}
                </p>
              ) : null}
              {routeError ? <p className="mt-2 text-xs text-rose-700">{routeError}</p> : null}
              <button
                type="button"
                onClick={handleUseMyLocation}
                disabled={!mapsReady || geoBusy}
                className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-700 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {geoBusy ? 'Locating…' : 'Use My Current Location'}
              </button>
            </div>
          </div>

          <div className="relative min-h-[360px] overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
            {mapsError ? (
              <div className="flex h-full items-center justify-center p-6 text-center text-sm text-slate-600">
                {mapsError}
              </div>
            ) : (
              <div ref={mapRef} className="h-full min-h-[360px] w-full" />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
